import React from "react";
import styled from "styled-components";

const AboutSection = styled.section`
  min-height: 80vh;
  position: relative;
  background: var(--background);
  padding-bottom: 2rem;
`;

const AboutContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1.5rem;
`;

const SectionTitle = styled.h2`
  font-size: 2.5rem;
  margin-bottom: 1rem;
  text-align: center;
  color: var(--text);

  span {
    color: var(--primary);
    position: relative;

    &:after {
      content: "";
      position: absolute;
      bottom: 5px;
      left: 0;
      width: 100%;
      height: 8px;
      background: linear-gradient(90deg, var(--primary), var(--accent));
      z-index: -1;
      opacity: 0.2;
    }
  }
`;

const SectionSubtitle = styled.p`
  font-size: 1.1rem;
  color: var(--textSecondary);
  text-align: center;
  max-width: 700px;
  margin: 0 auto 3rem;
`;

const FeaturesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled.div`
  background: ${(props) =>
    props.theme.colors.background === "#FFFFFF"
      ? "linear-gradient(135deg, rgba(239, 246, 255, 0.8), rgba(219, 234, 254, 0.8))"
      : "var(--background)"};
  border-radius: 20px;
  padding: 2rem;
  height: 100%;
  display: flex;
  flex-direction: column;
  transition: all 0.3s ease;
  box-shadow: ${(props) =>
    props.theme.colors.background === "#FFFFFF"
      ? "0 4px 20px rgba(37, 99, 235, 0.1)"
      : "0 4px 20px rgba(0, 0, 0, 0.2)"};

  &:hover {
    transform: translateY(-5px);
    box-shadow: ${(props) =>
      props.theme.colors.background === "#FFFFFF"
        ? "0 8px 30px rgba(37, 99, 235, 0.15)"
        : "0 8px 30px rgba(0, 0, 0, 0.3)"};
  }
`;

const FeatureIcon = styled.div`
  width: 60px;
  height: 60px;
  border-radius: 15px;
  background: linear-gradient(135deg, var(--primary), var(--secondary));
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1.5rem;
  transition: transform 0.3s ease;
  box-shadow: ${(props) =>
    props.theme.colors.background === "#FFFFFF"
      ? "0 8px 20px rgba(37, 99, 235, 0.2)"
      : "0 8px 20px rgba(30, 64, 175, 0.15)"};

  svg {
    width: 30px;
    height: 30px;
    color: white;
  }
`;

const FeatureTitle = styled.h3`
  font-size: 1.5rem;
  margin-bottom: 1rem;
  color: ${(props) =>
    props.theme.colors.background === "#FFFFFF" ? "#1E40AF" : "var(--text)"};
  position: relative;
  display: inline-block;

  &:after {
    content: "";
    position: absolute;
    bottom: -5px;
    left: 0;
    width: 0;
    height: 2px;
    background: linear-gradient(90deg, var(--primary), var(--secondary));
    transition: width 0.3s ease;
  }
`;

const FeatureDescription = styled.p`
  color: ${(props) =>
    props.theme.colors.background === "#FFFFFF"
      ? "#3B82F6"
      : "var(--textSecondary)"};
  line-height: 1.6;
  opacity: 0.9;
`;

function About() {
  const features = [
    {
      icon: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
          />
        </svg>
      ),
      title: "Razvoj inovacija",
      description:
        "Sa ciljem da mladima pružimo platformu za razvoj veština, umrežavanje sa stručnjacima iz industrije i stvaranje novih projekata, UNINP Hackathon predstavlja mesto gde se ideje pretvaraju u delo.",
      color1: "var(--primary)",
      color2: "var(--secondary)",
    },
    {
      icon: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
          />
        </svg>
      ),
      title: "Podrška našeg tima",
      description:
        "Naš tim organizatora čini grupa stručnjaka iz različitih oblasti, spremnih da vam pruže podršku tokom celog događaja. Sa bogatim iskustvom u organizaciji sličnih manifestacija, posvećeni smo tome da vam pružimo nezaboravno iskustvo i podstaknemo vas da ostvarite svoje najbolje ideje.",
      color1: "var(--secondary)",
      color2: "var(--accent)",
    },
    {
      icon: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7"
          />
        </svg>
      ),
      title: "Praktično Iskustvo",
      description:
        "Tokom hakatona, učesnici će imati priliku da rade na stvarnim projektima i izazovima, koristeći najnovije tehnologije i alate. Naš cilj je da vam omogućimo praktično iskustvo koje će vam pomoći da razvijete svoje veštine i znanje.",
      color1: "var(--accent)",
      color2: "var(--primary)",
    },
  ];

  return (
    <AboutSection id="o-nama">
      <AboutContainer>
        <SectionTitle>
          O<span> HACKATHONU</span>
        </SectionTitle>
        <SectionSubtitle>
          HACKATHON je jedinstveni događaj koji okuplja mlade talente iz
          oblasti programiranja, tehnologije i inovacija. Organizovan od strane
          Univerziteta u Novom Pazaru, naš hakaton je posvećen podsticanju
          kreativnosti, razmeni ideja i rešavanju realnih problema kroz
          tehnološka rešenja.
        </SectionSubtitle>

        <FeaturesGrid>
          {features.map((feature, index) => (
            <Card key={index}>
              <FeatureIcon>{feature.icon}</FeatureIcon>
              <FeatureTitle>{feature.title}</FeatureTitle>
              <FeatureDescription>{feature.description}</FeatureDescription>
            </Card>
          ))}
        </FeaturesGrid>
      </AboutContainer>
    </AboutSection>
  );
}

export default About;
